export const commercialWorkflow = [
  { code: 'INPUT', label: 'Input', description: 'Create one record or import a validated workbook.', href: '#/commercial-entry' },
  { code: 'VERIFY', label: 'Verify', description: 'Resolve calculation and data-coverage warnings.', href: '#/commercial-calculations' },
  { code: 'UNDERSTAND', label: 'Understand', description: 'Read revenue movement, retention and profitability.', href: '#/commercial-metrics' },
  { code: 'ACT', label: 'Act', description: 'Prioritise sales, customer and renewal work.', href: '#/commercial-sales' },
  { code: 'AUTOMATE', label: 'Automate', description: 'Configure and review safe operational runs.', href: '#/commercial-automation' },
  { code: 'AUDIT', label: 'Audit', description: 'Trace sensitive changes and automation decisions.', href: '#/commercial-audit' },
];

const guidance = {
  metrics: {
    purpose: 'Use this page to understand recurring-revenue health and explain what changed in the selected reporting period.',
    steps: ['Check whether the period is complete before comparing it.', 'Read retention and churn before interpreting net growth.', 'Use the waterfall to explain the movement from starting to ending MRR.'],
    terms: {
      MRR: 'Monthly recurring revenue: the normalized recurring value active in one month.',
      ARPA: 'Average revenue per account: current MRR divided by active customers.',
      'Gross retention': 'Revenue retained from existing customers, excluding expansion. Higher is better.',
      'Revenue churn': 'Starting recurring revenue lost to customer churn during the period.',
      'Customer churn': 'The percentage of starting customers that churned during the period.',
      'MRR waterfall': 'A reconciliation from starting MRR through new, reactivated, expanded, contracted and churned revenue to ending MRR.',
    },
    next: { label: 'Verify these calculations', href: '#/commercial-calculations' },
  },
  calculations: {
    purpose: 'Use this page to decide whether the commercial numbers are internally consistent and sufficiently complete for decisions.',
    steps: ['Start with Needs attention.', 'Compare observed and expected values to locate the gap.', 'Correct the source record, then return here to confirm the control passes.'],
    terms: {
      Observed: 'The value currently calculated from stored commercial data.',
      Expected: 'The value required by the reconciliation rule or coverage target.',
      Difference: 'Observed minus expected. A non-zero value may require investigation.',
      Warning: 'The mathematics may be valid, but operational data coverage is incomplete.',
      Failed: 'A mathematical identity or required validity rule is broken.',
    },
    next: { label: 'Correct source data', href: '#/commercial-entry' },
  },
  sales: {
    purpose: 'Use this page to decide which opportunity needs attention today and move qualified work toward customer onboarding.',
    steps: ['Filter to critical or high-priority opportunities.', 'Add a real contact and a dated next action.', 'Use Start onboarding when the deal is ready to become a customer.'],
    terms: {
      'Weighted pipeline': 'Potential ARR multiplied by the probability assigned to each opportunity.',
      Priority: 'A transparent score based on overdue work, inactivity and weighted value.',
      'Sales velocity': 'Expected pipeline value generated per day from deal value, win rate and cycle length.',
      Stage: 'The current position of the opportunity from prospecting through won or lost.',
    },
    next: { label: 'Review customer portfolio', href: '#/commercial-customers' },
  },
  customers: {
    purpose: 'Use this page to find a customer and open its complete revenue, renewal, contact and commercial history.',
    steps: ['Search by customer or country.', 'Select the customer name to open its detail page.', 'Use the history and contacts to prepare the next commercial action.'],
    terms: {
      'Current MRR': 'The customer recurring revenue in the latest imported month.',
      'Historical revenue': 'The sum of the customer monthly recurring-revenue snapshots in the available history.',
      'Commercial customer': 'The commercial record used for revenue analysis; it may link to a platform account.',
      'Platform account': 'The operational Admin account containing SUPPORT and LICENSE users.',
    },
    next: { label: 'Review upcoming renewals', href: '#/commercial-renewals' },
  },
  renewals: {
    purpose: 'Use this page to prioritise renewal conversations by time remaining and annual revenue at risk.',
    steps: ['Filter overdue or due-within-30-days work first.', 'Open the customer to review revenue history and contact details.', 'Record the latest renewal status, date, value and notes after every interaction.'],
    terms: {
      'ARR at risk': 'The annual recurring revenue exposed if the customer does not renew.',
      Overdue: 'The renewal date has passed without a completed renewal outcome.',
      Urgent: 'The renewal is due within 30 days.',
      'Time remaining': 'Calendar days until renewal; negative time is displayed as overdue.',
    },
    next: { label: 'Record a renewal update', href: '#/commercial-entry?type=renewal' },
  },
  entry: {
    purpose: 'Use this page for a single controlled change. Create the customer first, then add the records that depend on it.',
    steps: ['Choose the record type.', 'Select the customer when the record is customer-specific.', 'Complete required fields and save, then verify the result in the relevant workspace.'],
    terms: {
      'MRR movement': 'A dated replacement of the customer total MRR; the platform derives expansion, contraction or churn.',
      'Customer costs': 'Monthly direct delivery, support, sales and other costs attributed to one customer.',
      'Acquisition spend': 'Monthly channel spend and new-customer volume used for CAC calculations.',
      'Contract and subscription': 'The term, billing interval and recurring amount that normalize into MRR.',
    },
    next: { label: 'Verify data quality', href: '#/commercial-calculations' },
  },
  profitability: {
    purpose: 'Use this page to determine whether recurring revenue produces contribution profit after delivery, support, sales and acquisition costs.',
    steps: ['Check cost-data coverage before trusting margin outputs.', 'Add missing customer costs for the current month.', 'Add acquisition spend and new-customer counts to unlock CAC, payback and LTV:CAC.'],
    terms: {
      'Cost-data coverage': 'The percentage of active customers with current-period cost records.',
      'Gross margin': 'Revenue remaining after direct service or delivery costs.',
      'Contribution profit': 'Revenue remaining after direct, support, sales and other attributable costs.',
      CAC: 'Customer acquisition cost: acquisition spend divided by new customers for a channel and period.',
    },
    next: { label: 'Add customer costs', href: '#/commercial-entry?type=cost' },
  },
  imports: {
    purpose: 'Use this page for bulk customer or opportunity creation after reviewing how workbook columns map into the platform.',
    steps: ['Choose the dataset before selecting a file.', 'Review mapped values and blank cells in the preview.', 'Import only when the row count and mapping look correct, then review failures and calculation controls.'],
    terms: {
      Dataset: 'The kind of records to create; customer and opportunity files use different fields.',
      'Mapped column': 'A recognized workbook heading matched to a platform field.',
      'Preview row': 'A read-only normalized row; displaying it does not save anything.',
      Failure: 'A row rejected by server validation; its spreadsheet row number is reported.',
    },
    next: { label: 'Verify imported data', href: '#/commercial-calculations' },
  },
  automation: {
    purpose: 'Use this page to choose whether each workflow is started manually on its business page or automatically by its documented event, then review the read-only history.',
    steps: ['Read the Manual interaction and Automatic trigger on each workflow card.', 'Choose Manual or Automatic and save the mode.', 'Start Manual work in Sales, Customer detail, or Renewals; use this page only to review results.'],
    terms: {
      Manual: 'A user starts, reviews, and confirms the workflow on the page where the business task happens.',
      Automatic: 'The workflow starts from the event or schedule named on its card.',
      'Business page': 'Sales, Customer detail, or Renewals - the page that contains the data and action.',
      'Global kill switch': 'The emergency control that blocks every new Manual and Automatic workflow.',
    },
    next: { label: 'Review automation audit history', href: '#/commercial-audit' },
  },
  audit: {
    purpose: 'Use this page to answer who changed sensitive commercial state, what changed and which record was affected.',
    steps: ['Find the relevant date and actor.', 'Match the action to its entity type.', 'Use the originating workspace to investigate or correct the underlying record.'],
    terms: {
      Actor: 'The authenticated user responsible for the change; System identifies automated work.',
      Action: 'The recorded operation, such as creation, update, approval or execution.',
      Entity: 'The type of commercial record affected by the action.',
      'Audit history': 'An append-only trace used for accountability and investigation.',
    },
    next: { label: 'Return to Commercial overview', href: '#/commercial' },
  },
  customerDetail: {
    purpose: 'Use this page to understand one customer over time and prepare a revenue, renewal or relationship action with full context.',
    steps: ['Read current MRR, movement and renewal timing together.', 'Use contacts and the timeline to understand ownership and recent activity.', 'Record a comment or update the relevant commercial record after the interaction.'],
    terms: {
      'Active history': 'The first revenue-bearing month and number of active months in the selected range.',
      'Revenue movement': 'A dated change classified as new, reactivation, expansion, contraction or churn.',
      'Data coverage': 'Which sources are present and which account or contract links are still missing.',
      'Commercial timeline': 'Dated human notes that preserve relationship context alongside system data.',
    },
    next: { label: 'Record a renewal update', href: '#/commercial-entry?type=renewal' },
  },
};

export const getCommercialGuidance = section => guidance[section];

const entryTypes = new Set(['customer', 'opportunity', 'contract', 'revenue', 'renewal', 'cost', 'acquisition']);

export const parseEntryPreset = hash => {
  const query = String(hash || '').split('?')[1];
  if (!query) return { type: 'customer', customerId: '' };
  const params = new URLSearchParams(query);
  const type = params.get('type');
  if (!entryTypes.has(type)) return { type: 'customer', customerId: '' };
  return { type, customerId: params.get('customerId') || '' };
};
